/**
 * Horizontal scroll maths that survives `dir="rtl"`.
 *
 * `scrollLeft` is the one scroll property whose origin moves with the inline
 * axis. In an LTR container it runs 0..(scrollWidth - clientWidth), left to
 * right. In an RTL one every current browser reports the spec range instead:
 * 0 at the *right* edge, down to -(scrollWidth - clientWidth) at the left one.
 *
 * Code written against the LTR convention therefore reads a progress of 0
 * forever on /sa, and `scrollTo({ left: 0 })` lands on the logical *end*.
 *
 * This is for scrollers that carry a reading direction and must keep it — the
 * trial-beta instrument, whose Home/End keys a visitor actually uses. Purely
 * decorative strips take the other route and pin `dir='ltr'` on the scroller
 * itself, so no direction leaks in at all; see
 * `src/components/affiliates/affiliates-games.tsx`.
 */

/**
 * Resolves the inline direction the way this app declares it: `_document.tsx`
 * and `use-document-language.ts` both write a `dir` ATTRIBUTE on `<html>`, so
 * the nearest declared ancestor is the authoritative answer. The computed-style
 * fallback covers a scroller that is RTL through CSS alone, and `dir="auto"`,
 * which resolves per content rather than per attribute.
 */
function isRtlContainer(container: HTMLElement): boolean {
  const declared = container.closest('[dir]')?.getAttribute('dir')?.toLowerCase();
  if (declared === 'rtl' || declared === 'ltr') return declared === 'rtl';
  return container.ownerDocument.defaultView?.getComputedStyle(container).direction === 'rtl';
}

/** The maximum scrollable distance along the inline axis, as a non-negative number. */
function maxScroll(container: HTMLElement): number {
  return Math.max(0, container.scrollWidth - container.clientWidth);
}

/**
 * How far along its scrollable width the container sits: 0 at the logical start
 * (the right edge in RTL), 1 at the logical end.
 *
 * `Math.abs` is a no-op in LTR, where `scrollLeft` is already 0..max, and folds
 * the RTL range -max..0 onto that same scale with the same meaning — so no
 * direction check is needed to read progress.
 */
export function getRtlAwareScrollProgress(container: HTMLElement): number {
  const max = maxScroll(container);
  if (max === 0) return 0;
  return Math.min(1, Math.abs(container.scrollLeft) / max);
}

/**
 * Scrolls to the logical start or end of the inline axis. `'start'` is the
 * right edge in RTL and the left one in LTR, which is what a visitor pressing
 * Home expects in either direction.
 */
export function scrollToLogicalEdge(container: HTMLElement, edge: 'start' | 'end', behavior: ScrollBehavior): void {
  const distance = edge === 'start' ? 0 : maxScroll(container);
  container.scrollTo({ left: isRtlContainer(container) ? -distance : distance, behavior });
}
